import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { ActionButton } from "@components/ui";

import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_SendRecoveryPasswordLink } from "@i18n/usualTexts/t_recoveryPassword";
import { useAuthentication, useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { useIsMobileButtons } from "@shared/hooks/useIsMobileButtons";
import { SendRecoveryPasswordLinkRequest, isTimeoutError, isNetworkError } from "@features/authentication/services/OAuth2Request";
import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { TokenExpirationChecker } from "@shared/utils/token/TokenExpirationChecker";
import ErrorPage from "@pages/ErrorPage";
import i18n from "@i18n/index";

const normalizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

const getStorageKey = (baseKey: string, email: string): string => {
  const normalizedEmail = normalizeEmail(email);
  return `${baseKey}_${normalizedEmail}`;
};

const STORAGE_KEY_BASES = {
  SEND_COUNT: 'recovery_password_send_count',
  LAST_SEND_TIME: 'recovery_password_last_send_time',
  COOLDOWN_END_TIME: 'recovery_password_cooldown_end_time'
};

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const COOLDOWN_SECONDS = 84;

// --- Pure helper functions (no hooks, no state) ---

type ErrorPageState = {
  errorType: "route" | "oauth" | "general" | "timeout";
  errorCode?: string;
  errorMessage?: string;
  errorTitle?: string;
};

type ErrorExtractor = (error: any) => string | undefined;

const ERROR_EXTRACTORS: ErrorExtractor[] = [
  (e) => e?.body?.exception_uuid,
  (e) => e?.body?.title,
  (e) => e?.response?.data?.exception_uuid,
  (e) => e?.response?.data?.title,
  (e) => e?.message,
  (e) => (e instanceof Error ? e.message : String(e)),
];

function extractErrorMessage(error: any): string | null {
  for (const extractor of ERROR_EXTRACTORS) {
    const result = extractor(error);
    if (result) return result;
  }
  return null;
}

function handleTimeoutError(error: any): ErrorPageState | null {
  if (!isTimeoutError(error)) return null;
  return {
    errorType: 'timeout',
    errorMessage: error?.message || i18n.t('error.runtime.requestTimeout')
  };
}

function handleNetworkError(error: any): ErrorPageState | null {
  if (!isNetworkError(error)) return null;
  return {
    errorType: 'general',
    errorMessage: error?.message || i18n.t('error.runtime.networkError')
  };
}

function isBackendError(error: any): boolean {
  return !!(error?.body || error?.status);
}

function extractBodyField(body: Record<string, unknown> | undefined, field: string): string | undefined {
  return body?.[field] as string | undefined;
}

function buildBackendErrorState(error: any): ErrorPageState {
  const body = error?.body as Record<string, unknown> | undefined;
  return {
    errorType: 'oauth',
    errorCode: extractBodyField(body, 'error_code'),
    errorTitle: extractBodyField(body, 'title'),
    errorMessage: extractBodyField(body, 'detail') ?? extractBodyField(body, 'message')
  };
}

function handleBackendError(error: any): ErrorPageState | null {
  if (!isBackendError(error)) return null;
  return buildBackendErrorState(error);
}

function classifyError(error: any): ErrorPageState | null {
  return handleTimeoutError(error) ?? handleNetworkError(error) ?? handleBackendError(error);
}

function checkAndResetSendCount(
  storedCount: number,
  storedLastSendTime: string | null,
  sendCountKey: string,
  lastSendTimeKey: string
): number {
  if (!storedLastSendTime || storedCount <= 0) return storedCount;

  const timeSinceFirstSend = Date.now() - parseInt(storedLastSendTime);
  if (timeSinceFirstSend < TWENTY_FOUR_HOURS) return storedCount;

  console.log('[SendRecoveryPasswordLink] 24 hours have passed, resetting send count');
  localStorage.removeItem(sendCountKey);
  localStorage.removeItem(lastSendTimeKey);
  return 0;
}

function restoreCooldown(storedCooldownEndTime: string | null, cooldownEndTimeKey: string): number | null {
  if (!storedCooldownEndTime) return null;

  const cooldownEnd = parseInt(storedCooldownEndTime);
  if (cooldownEnd > Date.now()) return cooldownEnd;

  localStorage.removeItem(cooldownEndTimeKey);
  return null;
}

// --- Custom hooks ---

function useSendRecoveryState(userEmail: string) {
  const [sendCount, setSendCount] = useState(0);
  const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
  const [remainingCooldown, setRemainingCooldown] = useState(0);
  const [isMaxSendsReached, setIsMaxSendsReached] = useState(false);
  const [hasClickedSend, setHasClickedSend] = useState(false);
  const [isStateInitialized, setIsStateInitialized] = useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  useEffect(() => {
    if (!userEmail) return;

    const sendCountKey = getStorageKey(STORAGE_KEY_BASES.SEND_COUNT, userEmail);
    const cooldownEndTimeKey = getStorageKey(STORAGE_KEY_BASES.COOLDOWN_END_TIME, userEmail);
    const lastSendTimeKey = getStorageKey(STORAGE_KEY_BASES.LAST_SEND_TIME, userEmail);

    const storedSendCount = parseInt(localStorage.getItem(sendCountKey) || '0');
    const storedLastSendTime = localStorage.getItem(lastSendTimeKey);
    const storedCooldownEndTime = localStorage.getItem(cooldownEndTimeKey);

    const actualSendCount = checkAndResetSendCount(storedSendCount, storedLastSendTime, sendCountKey, lastSendTimeKey);
    setSendCount(actualSendCount);
    setIsMaxSendsReached(actualSendCount >= 3);

    const restoredCooldownEnd = restoreCooldown(storedCooldownEndTime, cooldownEndTimeKey);
    if (restoredCooldownEnd) {
      setCooldownEndTime(restoredCooldownEnd);
      setRemainingCooldown(Math.ceil((restoredCooldownEnd - Date.now()) / 1000));
    }

    setIsStateInitialized(true);
  }, [userEmail]);

  useEffect(() => {
    if (!cooldownEndTime) return;

    const timer = setInterval(() => {
      const remaining = Math.ceil((cooldownEndTime - Date.now()) / 1000);

      if (remaining <= 0) {
        setCooldownEndTime(null);
        setRemainingCooldown(0);
        if (userEmail) {
          localStorage.removeItem(getStorageKey(STORAGE_KEY_BASES.COOLDOWN_END_TIME, userEmail));
        }
        clearInterval(timer);
      } else {
        setRemainingCooldown(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownEndTime, userEmail]);

  useEffect(() => {
    if (sendCount >= 3 && !isRequestInProgress) {
      setIsMaxSendsReached(true);
    }
  }, [sendCount, isRequestInProgress]);

  const incrementSendCount = useCallback(() => {
    if (!userEmail) return;

    const newCount = sendCount + 1;
    setSendCount(newCount);
    const sendCountKey = getStorageKey(STORAGE_KEY_BASES.SEND_COUNT, userEmail);
    localStorage.setItem(sendCountKey, newCount.toString());

    if (newCount === 1) {
      const lastSendTimeKey = getStorageKey(STORAGE_KEY_BASES.LAST_SEND_TIME, userEmail);
      localStorage.setItem(lastSendTimeKey, Date.now().toString());
      console.log('[SendRecoveryPasswordLink] First send recorded, will reset after 24 hours');
    }
  }, [sendCount, userEmail]);

  const startCooldown = useCallback(() => {
    if (!userEmail) return;

    const cooldownEnd = Date.now() + (COOLDOWN_SECONDS * 1000);
    setCooldownEndTime(cooldownEnd);
    setRemainingCooldown(COOLDOWN_SECONDS);
    const cooldownEndTimeKey = getStorageKey(STORAGE_KEY_BASES.COOLDOWN_END_TIME, userEmail);
    localStorage.setItem(cooldownEndTimeKey, cooldownEnd.toString());
  }, [userEmail]);

  return {
    sendCount, cooldownEndTime, remainingCooldown, isMaxSendsReached,
    hasClickedSend, setHasClickedSend,
    isStateInitialized,
    isRequestInProgress, setIsRequestInProgress,
    incrementSendCount, startCooldown,
  };
}

function useTokenValidation() {
  const navigate = useNavigate();
  const { authData } = useAuthentication();

  useEffect(() => {
    const token = authData.identityRequestResponse;
    if (!token?.access_token || TokenExpirationChecker.isTokenExpired(token, 30)) {
      console.warn('[SendRecoveryPasswordLink] T2 token missing or expired, redirecting to identity step');
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY, {
        sessionExpired: 'true'
      }), { replace: true });
    }
  }, [authData.identityRequestResponse, navigate]);
}

// --- Validators (each CYC 1-2) ---

function validateMaxSends(sendCount: number, setIsMaxSendsReached: (v: boolean) => void): void {
  if (sendCount < 3) return;
  console.log('[SendRecoveryPasswordLink] Maximum sends reached');
  setIsMaxSendsReached(true);
  throw new Error(i18n.t("error.runtime.maxRecoverySends"));
}

function validateCooldown(cooldownEndTime: number | null, remainingCooldown: number): void {
  if (!cooldownEndTime || remainingCooldown <= 0) return;
  console.log('[SendRecoveryPasswordLink] Still in cooldown period');
  throw new Error(i18n.t("error.runtime.recoveryCooldown", { seconds: remainingCooldown }));
}

function validateNotInProgress(isRequestInProgress: boolean): void {
  if (!isRequestInProgress) return;
  console.log('[SendRecoveryPasswordLink] Request already in progress, ignoring duplicate call');
  throw new Error(i18n.t("error.runtime.requestInProgress"));
}

function validateToken(accessToken: string | undefined): void {
  if (accessToken) return;
  console.error('[SendRecoveryPasswordLink] No T2 token found');
  throw new Error(i18n.t("error.runtime.noIdentityToken"));
}

function validateOAuth2Data(oauth2Data: any): void {
  if (oauth2Data?.client_id && oauth2Data?.redirect_uri) return;
  console.error('[SendRecoveryPasswordLink] OAuth2 data is missing or incomplete');
  throw new Error(i18n.t("error.runtime.oauth2Required"));
}

// --- Component ---

type ButtonTextKey = 'maxReached' | 'waiting' | 'sending' | 'default';

function getButtonTextKey(isMaxSendsReached: boolean, cooldownEndTime: number | null, remainingCooldown: number, isRequestInProgress: boolean): ButtonTextKey {
  if (isMaxSendsReached) return 'maxReached';
  if (cooldownEndTime && remainingCooldown > 0) return 'waiting';
  if (isRequestInProgress) return 'sending';
  return 'default';
}

const BUTTON_TEXT_MAP: Record<ButtonTextKey, string> = {
  maxReached: t_SendRecoveryPasswordLink.buttonMaxReachedText,
  waiting: t_SendRecoveryPasswordLink.buttonWaitText,
  sending: t_SendRecoveryPasswordLink.buttonSendingText,
  default: t_SendRecoveryPasswordLink.buttonText,
};

function computeButtonDisabled(isMaxSendsReached: boolean, cooldownEndTime: number | null, remainingCooldown: number, isRequestInProgress: boolean): boolean {
  if (isMaxSendsReached) return true;
  if (isRequestInProgress) return true;
  return !!cooldownEndTime && remainingCooldown > 0;
}

function computeButtonText(t: (key: string, opts?: any) => string, buttonTextKey: ButtonTextKey, remainingCooldown: number): string {
  if (buttonTextKey === 'waiting') {
    return t(BUTTON_TEXT_MAP[buttonTextKey], { seconds: remainingCooldown });
  }
  return t(BUTTON_TEXT_MAP[buttonTextKey]);
}

function computeButtonVariant(isMaxSendsReached: boolean): "secondary" | "primary" {
  return isMaxSendsReached ? "secondary" : "primary";
}

function getUserEmail(username: string | undefined): string {
  return username || '';
}

const EmailMessage: React.FC<{ hasClickedSend: boolean; t: (key: string, opts?: any) => string; username: string }> = ({ hasClickedSend, t, username }) => {
  if (hasClickedSend) {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--accent-primary)' }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p className="text-sm text-center font-semibold" style={{ color: 'var(--text-primary)' }}>
          {t(t_SendRecoveryPasswordLink.recoveryLinkSentTitle)}
        </p>
        <p className="text-sm text-center leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {t(t_SendRecoveryPasswordLink.text, { email: username })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--accent-primary)' }}>
          <rect width="20" height="16" x="2" y="4" rx="2"/>
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
        </svg>
      </div>
      <p className="text-sm text-center leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {t(t_SendRecoveryPasswordLink.initialText, { email: username })}
      </p>
    </div>
  );
};

async function executeSendRequest(
  userEmail: string,
  sessionState: string,
  identityToken: string | undefined,
  antiBotToken: string | undefined,
  oauth2Data: any,
  incrementSendCount: () => void,
  startCooldown: () => void,
  setHasClickedSend: (v: boolean) => void,
  ongoingRequestRef: React.MutableRefObject<Promise<any> | null>,
  setIsRequestInProgress: (v: boolean) => void,
): Promise<any> {
  try {
    const response = await SendRecoveryPasswordLinkRequest(
      userEmail, sessionState, identityToken, antiBotToken, oauth2Data
    );
    incrementSendCount();
    startCooldown();
    setHasClickedSend(true);
    return response;
  } catch (error) {
    console.error('[SendRecoveryPasswordLink] Send recovery password error:', error);
    throw error;
  } finally {
    ongoingRequestRef.current = null;
    setIsRequestInProgress(false);
  }
}

function renderErrorPage(errorState: ErrorPageState): React.ReactElement {
  return (
    <ErrorPage
      errorType={errorState.errorType}
      errorCode={errorState.errorCode}
      errorMessage={errorState.errorMessage}
      errorTitle={errorState.errorTitle}
    />
  );
}

function handleRecoveryError(
  error: any,
  isAutoSend: boolean,
  setErrorPageState: React.Dispatch<React.SetStateAction<ErrorPageState | null>>,
  setServerErrorPopUp: React.Dispatch<React.SetStateAction<string | null>>
): void {
  console.error('[SendRecoveryPasswordLink] Error handling:', error);

  const classifiedError = classifyError(error);
  if (classifiedError) {
    setErrorPageState(classifiedError);
    return;
  }

  if (isAutoSend) return;

  const errorMessage = extractErrorMessage(error);
  if (errorMessage) {
    setServerErrorPopUp(errorMessage);
  }
}

function deduplicateRequest(
  ref: React.MutableRefObject<Promise<any> | null>,
  execute: () => Promise<any>
): Promise<any> {
  if (ref.current) return ref.current;
  return execute();
}

function resolveMobileFooter(isMobile: boolean, button: React.ReactNode): React.ReactNode | undefined {
  if (!isMobile) return undefined;
  return <div className="w-full">{button}</div>;
}

const MaxSendsAlert: React.FC<{ show: boolean; text: string }> = ({ show, text }) => {
  if (!show) return null;
  return (
    <div className="bg-red-100 text-red-700 text-center mb-3 p-3 rounded">
      {text}
    </div>
  );
};

const SendRecoveryPasswordLinkPage: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t("login.sendRecoveryPasswordLink.pageTitle"));
  const isMobileButtons = useIsMobileButtons();

  const { authData } = useAuthentication();
  const { state, oauth2Data } = useOAuth2();
  const { currentToken: antiBotToken, executeRecaptcha } = useRecaptchaContext();

  const ongoingRequestRef = useRef<Promise<any> | null>(null);

  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [errorPageState, setErrorPageState] = useState<ErrorPageState | null>(null);

  const userEmail = getUserEmail(authData.username);

  const {
    sendCount, cooldownEndTime, remainingCooldown, isMaxSendsReached,
    hasClickedSend, setHasClickedSend,
    isRequestInProgress, setIsRequestInProgress,
    incrementSendCount, startCooldown,
  } = useSendRecoveryState(userEmail);

  useTokenValidation();

  const validateSendRequest = useCallback((): void => {
    validateMaxSends(sendCount, () => { /* handled by effect */ });
    validateCooldown(cooldownEndTime, remainingCooldown);
    validateNotInProgress(isRequestInProgress);
    validateToken(authData.identityRequestResponse?.access_token);
    validateOAuth2Data(oauth2Data);
  }, [sendCount, cooldownEndTime, remainingCooldown, isRequestInProgress, authData.identityRequestResponse, oauth2Data]);

  const sendLinkEmail = useCallback(async () => {
    return deduplicateRequest(ongoingRequestRef, async () => {
      validateSendRequest();
      const identityToken = authData.identityRequestResponse?.access_token;
      
      let token = antiBotToken;
      if (!token && executeRecaptcha) {
        const result = await executeRecaptcha();
        if (result.success && result.token) {
          token = result.token;
        }
      }

      setIsRequestInProgress(true);
      const requestPromise = executeSendRequest(
        userEmail, state, identityToken, token || undefined, oauth2Data,
        incrementSendCount, startCooldown, setHasClickedSend, ongoingRequestRef, setIsRequestInProgress
      );
      ongoingRequestRef.current = requestPromise;
      return requestPromise;
    });
  }, [authData.identityRequestResponse, userEmail, state, incrementSendCount, startCooldown, antiBotToken, executeRecaptcha, oauth2Data, validateSendRequest, setIsRequestInProgress, setHasClickedSend]);

  const handleError = useCallback((error: any, isAutoSend: boolean = false) => {
    handleRecoveryError(error, isAutoSend, setErrorPageState, setServerErrorPopUp);
  }, []);

  const handleSendButtonClick = async () => {
    setServerErrorPopUp(null);
    setErrorPageState(null);

    try {
      await sendLinkEmail();
    } catch (error) {
      handleError(error, false);
    }
  };

  const isButtonDisabled = computeButtonDisabled(isMaxSendsReached, cooldownEndTime, remainingCooldown, isRequestInProgress);
  const buttonTextKey = getButtonTextKey(isMaxSendsReached, cooldownEndTime, remainingCooldown, isRequestInProgress);
  const buttonText = computeButtonText(t, buttonTextKey, remainingCooldown);
  const buttonVariant = computeButtonVariant(isMaxSendsReached);

  if (errorPageState) {
    return renderErrorPage(errorPageState);
  }

  const sendButton = (
    <ActionButton
      isLoading={isRequestInProgress}
      disabled={isButtonDisabled}
      variant={buttonVariant}
      onClick={handleSendButtonClick}
      className={isMobileButtons ? "h-11 w-full" : "w-full"}
    >
      {buttonText}
    </ActionButton>
  );

  const mobileFooter = resolveMobileFooter(isMobileButtons, sendButton);

  return (
    <AuthenticationForm
      title={t(t_SendRecoveryPasswordLink.title)}
      subtitle={t(t_SendRecoveryPasswordLink.subtitle)}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
      mobileFooter={mobileFooter}
      hideClientHeader
      centerContent
    >
      <EmailMessage hasClickedSend={hasClickedSend} t={t} username={authData.username ?? ""} />

      <MaxSendsAlert show={isMaxSendsReached} text={t(t_SendRecoveryPasswordLink.maxSendsMessage)} />

      {!isMobileButtons && (
        <div className="w-full">
          {sendButton}
        </div>
      )}
    </AuthenticationForm>
  );
};

export default SendRecoveryPasswordLinkPage;
